import React from 'react';
import { Box, Grid2 } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import {
  useGetClinicianDetailsById,
  useGetClinicianMetaData,
} from '../../API/hooks/ClinicianHook';
import {
  CustomDivider,
  CustomIconButton,
  CustomTypography,
} from '@qler-view/shared_lib/src';
import BackIcon from '@assets/images/BackIcon.svg';
import {
  CLINICIANS_ROUTE_PATH,
  NEW_CLINICIANS_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomButton from '@ui-components/Button/CustomButton';
import { hasRole } from '../login/storageUtility';

const ClinicianDetailsView: React.FC = () => {
  const navigate = useNavigate();

  const userId = localStorage.getItem('userId') ?? '0';
  const { clinicianId } = useParams<{ clinicianId?: string }>();
  const { clinicianMetaData } = useGetClinicianMetaData(userId);
  const { ClinicianDetailsById, isLoading } = useGetClinicianDetailsById(
    userId,
    Number(clinicianId)
  );

  const handleEditClinicianInfo = (clinicianId: number) => {
    navigate(NEW_CLINICIANS_ROUTE_PATH + `/${clinicianId}`, { replace: true });
  };

  const displayValueOrNA = (value: unknown): string => {
    if (
      value === null ||
      value === undefined ||
      value === 0 ||
      value === '0' ||
      value === ''
    ) {
      return 'N/A';
    }
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return String(value);
    }
    return 'N/A';
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }
  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className=" flex-justify-space-between align-items-baseline">
              <Box className="text-align-left display-flex">
                <CustomIconButton
                  id="back-button"
                  icon={<BackIcon />}
                  onClick={() => {
                    navigate(CLINICIANS_ROUTE_PATH);
                  }}
                ></CustomIconButton>

                <CustomTypography
                  className="pl-8"
                  variant="h4"
                  id="add-new-facility-text"
                >
                  {'View Clinician'}
                </CustomTypography>
                <CustomTypography
                  className="pl-12"
                  variant="h4"
                  id="partner-business-id"
                >
                  {clinicianId
                    ? `(${ClinicianDetailsById?.clinicianRequest.userInfo.userBusinessId})`
                    : ''}
                </CustomTypography>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box>
                  <CustomButton
                    id="save-button"
                    onClick={() => {
                      handleEditClinicianInfo(
                        ClinicianDetailsById?.clinicianRequest.userInfo.id ?? 0
                      );
                    }}
                    variant="contained"
                  >
                    Edit
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      <Box className="form-background p-24">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <CustomTypography
              variant="h5"
              className="text-align-left pb-32"
              id="user-info-text"
            >
              User Information
            </CustomTypography>
          </Grid2>
        </Grid2>
        <Box className="display-flex">
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="clinician-first-name-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                First Name
              </CustomTypography>
              <CustomTypography
                id="clinician-first-name-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo.firstName ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="clinician-last-name-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Last Name
              </CustomTypography>
              <CustomTypography
                id="clinician-last-name-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo.lastName ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="clinician-status-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Status
              </CustomTypography>
              <Box className="text-align-left">
                {ClinicianDetailsById?.clinicianRequest.userInfo.status ? (
                  <Box className="display-flex align-items-baseline">
                    <Box component="span" className="status-active-dot " />
                    <CustomTypography
                      id="clinician-status-active-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pl-4"
                    >
                      {' '}
                      Active
                    </CustomTypography>
                  </Box>
                ) : (
                  <Box className="display-flex align-items-baseline">
                    <Box component="span" className="status-inactive-dot " />
                    <CustomTypography
                      id="clinician-status-inactive-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pl-4"
                    >
                      {' '}
                      Inactive
                    </CustomTypography>
                  </Box>
                )}
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="preferred-name-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Preferred Name
              </CustomTypography>
              <CustomTypography
                id="preferred-name-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo
                  .preferredName || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="suffix-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Suffix
              </CustomTypography>
              <CustomTypography
                id="suffix-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left pb-12"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo.suffix ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="category-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Category
              </CustomTypography>
              <CustomTypography
                id="category-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left "
              >
                {clinicianMetaData?.clinicianCategories.find(
                  (category) =>
                    category.id ===
                    ClinicianDetailsById?.clinicianRequest.userInfo.categoryId
                )?.name || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="max-concurrent-consults-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Max Concurrent Consults
              </CustomTypography>
              <CustomTypography
                id="max-concurrent-consults-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left pb-12"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo
                  .maxConcurrentConsults || 'N/A'}
              </CustomTypography>
            </Grid2>
          </Grid2>
        </Box>
        <Box className="pt-32">
          <CustomDivider />
        </Box>
        <Box className="pt-32">
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <CustomTypography
                variant="h5"
                className="text-align-left pb-32"
                id="provider-info-text"
              >
                Provider Info
              </CustomTypography>
            </Grid2>
          </Grid2>
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="clinician-email-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Email
              </CustomTypography>
              <CustomTypography
                id="clinician-email-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo.email || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="clinician-primary-phone-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Primary Phone
              </CustomTypography>
              <CustomTypography
                id="clinician-primary-phone-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo.phone || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="clinician-npi-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                NPI
              </CustomTypography>
              <CustomTypography
                id="clinician-npi-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left pb-12"
              >
                {ClinicianDetailsById?.clinicianRequest.providerDetails.npi ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="alternate-email-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Alternate Email
              </CustomTypography>
              <CustomTypography
                id="alternate-email-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo
                  .alternateEmail || 'N/A'}
              </CustomTypography>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="clinician-alternate-phone-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Alternate Phone
              </CustomTypography>
              <CustomTypography
                id="clinician-alternate-phone-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo
                  .alternatePhone || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="clinician-gender-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Gender
              </CustomTypography>
              <CustomTypography
                id="clinician-gender-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {ClinicianDetailsById?.clinicianRequest.userInfo.gender}
              </CustomTypography>
            </Grid2>
          </Grid2>
        </Box>
        <Box className="pt-32">
          <CustomDivider />
        </Box>
        <Box className="pt-32">
          <Box>
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <CustomTypography
                  variant="h5"
                  className="text-align-left pb-32"
                  id="clinician-address-details-text"
                >
                  Address Details
                </CustomTypography>
              </Grid2>
            </Grid2>
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="clinician-mailing-address-line-1-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Address Line 1
                </CustomTypography>
                <CustomTypography
                  id="clinician-mailing-address-line-1-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {ClinicianDetailsById?.clinicianRequest.address
                    .addressLine1 || 'N/A'}
                </CustomTypography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="clinician-mailing-city-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  City
                </CustomTypography>
                <CustomTypography
                  id="clinician-mailing-city-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {ClinicianDetailsById?.clinicianRequest.address.city || 'N/A'}
                </CustomTypography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="clinician-mailing-postal-code-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Postal Code
                </CustomTypography>
                <CustomTypography
                  id="clinician-mailing-postal-code-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {ClinicianDetailsById?.clinicianRequest.address.postalCode ||
                    'N/A'}
                </CustomTypography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="clinician-mailing-address-line-2-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Address Line 2
                </CustomTypography>
                <CustomTypography
                  id="clinician-mailing-address-line-2-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {ClinicianDetailsById?.clinicianRequest.address
                    .addressLine2 || 'N/A'}
                </CustomTypography>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="clinician-mailing-state-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  State
                </CustomTypography>
                <CustomTypography
                  id="clinician-mailing-state-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {displayValueOrNA(
                    ClinicianDetailsById?.clinicianRequest.address.state
                  )}
                </CustomTypography>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="clinician-mailing-county-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  County
                </CustomTypography>
                <CustomTypography
                  id="clinician-mailing-county-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {ClinicianDetailsById?.clinicianRequest.address.county ||
                    'N/A'}
                </CustomTypography>
              </Grid2>
            </Grid2>
          </Box>

          <Box className="pt-32">
            <CustomDivider />
          </Box>
          <Box className="pt-32">
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <CustomTypography
                  variant="h5"
                  className="text-align-left pb-32"
                  id="provider-notes-text"
                >
                  Provider Notes
                </CustomTypography>
              </Grid2>
            </Grid2>
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                <CustomTypography
                  id="provider-notes-label"
                  variant="body3"
                  color="textDisabled"
                  className="text-align-left pb-12"
                >
                  Notes
                </CustomTypography>
                <CustomTypography
                  id="provider-notes-value"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left"
                >
                  {ClinicianDetailsById?.clinicianRequest.userInfo.notes ||
                    'N/A'}
                </CustomTypography>
              </Grid2>
            </Grid2>
          </Box>
          {/* <Box className="pt-32">
            <CustomDivider />
          </Box>
          <Box className="pt-32">
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <CustomTypography
                  variant="h5"
                  className="text-align-left pb-32"
                  id="clinician-profile-picture-text"
                >
                  Profile Picture
                </CustomTypography>
              </Grid2>
            </Grid2>
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="clinician-profile-picture-label"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left pb-12"
                >
                  Profile Picture
                </CustomTypography>
              </Grid2>
            </Grid2>
          </Box> */}
          <Box className="pt-32">
            <CustomDivider />
          </Box>
          <Box className="pt-32">
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <CustomTypography
                  variant="h5"
                  className="text-align-left pb-32"
                  id="clinician-zoom-details-text"
                >
                  Zoom Details
                </CustomTypography>
              </Grid2>
            </Grid2>
            <Grid2 container spacing={4} className="width-80">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="clinician-zoom-user-id-label"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left pb-12"
                >
                  Zoom User ID
                </CustomTypography>
                <CustomTypography
                  id="zoom-user-id"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left pb-12"
                >
                  {
                    ClinicianDetailsById?.clinicianRequest.providerDetails
                      .zoomUserId
                  }
                </CustomTypography>
              </Grid2>
            </Grid2>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ClinicianDetailsView;
